'use client'

import EventCard from '@/components/_cards/event/event-card'
import MagnifyingGlassIcon from '@/components/icons/magnifying-glass'
import { Accordion } from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { dayOptions } from '@/helpers/days'
import {
  buildSearchParamsObject,
  RACING_SORT_OPTIONS,
} from '@/lib/server/sim-racing-games/schemas'
import { cn } from '@/lib/utils'
import { usePathname, useRouter } from 'next/navigation'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react'
import { loadMoreRacing } from '../actions/load-more'
import FilterCheckbox from './checkbox'
import Filter from './filter'
import MobileFilterWrapper, {
  AccordionItemWrapper,
} from './mobile-filter-wrapper'

const RACE_TYPE_OPTIONS = [
  { value: 'single', label: 'Single Event' },
  { value: 'championship', label: 'Championship Event' },
]

const SORT_LABELS = {
  live: 'Live',
  upcoming: 'Upcoming',
  finished: 'Finished',
}

export default function RacingClientPage({
  game,
  initialItems,
  initialTotal,
  initialNextCursor,
  appliedFilter,
  gameName,
  session,
}) {
  const pathname = usePathname()
  const router = useRouter()
  const loaderRef = useRef(null)
  const [isPending, startTransition] = useTransition()

  const [items, setItems] = useState(initialItems)
  const [total, setTotal] = useState(initialTotal)
  const [nextCursor, setNextCursor] = useState(initialNextCursor)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const [draft, setDraft] = useState(appliedFilter)

  const platformOptions = useMemo(
    () => game.platforms.map((entry) => entry.platform),
    [game.platforms],
  )

  const carClassOptions = useMemo(() => {
    const seen = new Set()

    return game.carClasses
      .map((entry) => entry?.name?.trim())
      .filter((name) => {
        if (!name) return false

        const key = name.toLowerCase()
        if (seen.has(key)) return false

        seen.add(key)
        return true
      })
      .map((name) => ({ name }))
  }, [game.carClasses])

  useEffect(() => {
    setItems(initialItems)
    setTotal(initialTotal)
    setNextCursor(initialNextCursor)
    setDraft(appliedFilter)
  }, [appliedFilter, initialItems, initialNextCursor, initialTotal])

  const hasMore = Boolean(nextCursor)

  const navigateWithFilter = useCallback(
    (nextFilter) => {
      const params = buildSearchParamsObject(nextFilter)
      const query = params.toString()
      startTransition(() => {
        router.push(query ? `${pathname}?${query}` : pathname)
      })
    },
    [pathname, router],
  )

  const applyDraft = useCallback(() => {
    navigateWithFilter(draft)
  }, [draft, navigateWithFilter])

  const clearDraft = useCallback(() => {
    const reset = {
      q: '',
      sort: 'live',
      platforms: [],
      raceTypes: [],
      carClasses: [],
      days: [],
      cursor: undefined,
    }

    setDraft(reset)
    startTransition(() => {
      router.push(pathname)
    })
  }, [pathname, router])

  const isRefreshing = isPending

  const loadMore = useCallback(async () => {
    if (!nextCursor || isLoadingMore) return

    setIsLoadingMore(true)

    try {
      const result = await loadMoreRacing({
        gameName,
        filter: appliedFilter,
        cursor: nextCursor,
      })

      setItems((previous) => [...previous, ...result.items])
      setTotal(result.total)
      setNextCursor(result.nextCursor)
    } finally {
      setIsLoadingMore(false)
    }
  }, [appliedFilter, gameName, isLoadingMore, nextCursor])

  useEffect(() => {
    if (!loaderRef.current || !hasMore) return

    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore()
        }
      },
      {
        threshold: 0.2,
      },
    )

    observer.observe(loaderRef.current)

    return () => observer.disconnect()
  }, [hasMore, loadMore])

  return (
    <>
      <MobileFilterWrapper onClear={clearDraft} onApply={applyDraft}>
        <Accordion type="single" collapsible className="w-full">
          {platformOptions.length > 1 && (
            <AccordionItemWrapper value="platforms" title="Platforms">
              {platformOptions.map((platform) => (
                <FilterCheckbox
                  key={platform.name}
                  checked={draft.platforms.includes(platform.name)}
                  label={platform.nicename}
                  onChange={() => {
                    setDraft((previous) => ({
                      ...previous,
                      platforms: previous.platforms.includes(platform.name)
                        ? previous.platforms.filter(
                            (value) => value !== platform.name,
                          )
                        : [...previous.platforms, platform.name],
                    }))
                  }}
                />
              ))}
            </AccordionItemWrapper>
          )}

          <AccordionItemWrapper value="race-types" title="Race Types">
            {RACE_TYPE_OPTIONS.map((raceType) => (
              <FilterCheckbox
                key={raceType.value}
                checked={draft.raceTypes.includes(raceType.value)}
                label={raceType.label}
                onChange={() => {
                  setDraft((previous) => ({
                    ...previous,
                    raceTypes: previous.raceTypes.includes(raceType.value)
                      ? previous.raceTypes.filter(
                          (value) => value !== raceType.value,
                        )
                      : [...previous.raceTypes, raceType.value],
                  }))
                }}
              />
            ))}
          </AccordionItemWrapper>

          <AccordionItemWrapper value="car-classes" title="Car Classes">
            {carClassOptions.map((carClass) => (
              <FilterCheckbox
                key={carClass.name}
                checked={draft.carClasses.includes(carClass.name)}
                label={carClass.name}
                onChange={() => {
                  setDraft((previous) => ({
                    ...previous,
                    carClasses: previous.carClasses.includes(carClass.name)
                      ? previous.carClasses.filter(
                          (value) => value !== carClass.name,
                        )
                      : [...previous.carClasses, carClass.name],
                  }))
                }}
              />
            ))}
          </AccordionItemWrapper>

          <AccordionItemWrapper value="days" title="Days">
            {dayOptions.map((day) => (
              <FilterCheckbox
                key={day.name}
                checked={draft.days.includes(day.name)}
                label={day.nicename}
                onChange={() => {
                  setDraft((previous) => ({
                    ...previous,
                    days: previous.days.includes(day.name)
                      ? previous.days.filter((value) => value !== day.name)
                      : [...previous.days, day.name],
                  }))
                }}
              />
            ))}
          </AccordionItemWrapper>

          <AccordionItemWrapper value="sort" title="Sort By">
            {RACING_SORT_OPTIONS.map((sortValue) => (
              <FilterCheckbox
                key={sortValue}
                checked={draft.sort === sortValue}
                label={SORT_LABELS[sortValue]}
                onChange={() =>
                  setDraft((previous) => ({ ...previous, sort: sortValue }))
                }
              />
            ))}
          </AccordionItemWrapper>
        </Accordion>
      </MobileFilterWrapper>

      <div className="col-span-full mt-6 hidden items-center justify-between desktop-xs:col-span-10 desktop-xs:col-start-2 desktop-xs:flex">
        <div className="flex items-center gap-3">
          <p className="text-p-1 text-neutral-blue-500">Filter</p>
          <div className="flex flex-wrap gap-3">
            {platformOptions.length > 1 && (
              <Filter
                value={draft.platforms}
                setValue={(platforms) =>
                  setDraft((previous) => ({ ...previous, platforms }))
                }
                options={platformOptions}
                displayLabel="nicename"
                valueLabel="name"
                description="Platforms"
                parentClassName="w-[157px]"
                displayClassName="w-[200px]"
                multi
              />
            )}
            <Filter
              value={draft.raceTypes}
              setValue={(raceTypes) =>
                setDraft((previous) => ({ ...previous, raceTypes }))
              }
              options={RACE_TYPE_OPTIONS}
              displayLabel="label"
              valueLabel="value"
              description="Race Types"
              parentClassName="w-[170px]"
              displayClassName="w-[200px]"
              multi
            />
            {carClassOptions.length > 1 && (
              <Filter
                value={draft.carClasses}
                setValue={(carClasses) =>
                  setDraft((previous) => ({ ...previous, carClasses }))
                }
                options={carClassOptions}
                displayLabel="name"
                valueLabel="name"
                description="Car Classes"
                parentClassName="w-[172px]"
                displayClassName="w-[360px]"
                multi
              />
            )}
            <Filter
              value={draft.days}
              setValue={(days) =>
                setDraft((previous) => ({ ...previous, days }))
              }
              options={dayOptions}
              displayLabel="nicename"
              valueLabel="name"
              description="Days"
              parentClassName="w-[125px]"
              displayClassName="w-[200px]"
              multi
            />
            <Filter
              value={draft.sort}
              setValue={(sort) =>
                setDraft((previous) => ({ ...previous, sort }))
              }
              options={RACING_SORT_OPTIONS.map((value) => ({
                value,
                label: SORT_LABELS[value],
              }))}
              displayLabel="label"
              valueLabel="value"
              parentClassName="w-[125px]"
              displayClassName="w-[200px]"
            />
            <Button variant="secondary" onClick={clearDraft}>
              Clear
            </Button>
            <Button onClick={applyDraft}>Apply</Button>
          </div>
        </div>

        <div className="relative flex w-[392px] items-center">
          <Input
            className="pl-12"
            placeholder="Search Racing"
            value={draft.q}
            onChange={(event) =>
              setDraft((previous) => ({
                ...previous,
                q: event.target.value,
              }))
            }
            onKeyDown={(event) => {
              if (event.key === 'Enter') applyDraft()
            }}
          />
          <MagnifyingGlassIcon className="pointer-events-none absolute ml-6 h-4 w-4 stroke-shades-0 stroke-2" />
        </div>
      </div>

      <div className="col-span-full mt-6 grid gap-5 desktop-xs:col-span-10 desktop-xs:col-start-2 desktop-xs:grid-cols-2 desktop-md:grid-cols-3 desktop-lg:gap-9">
        {isRefreshing && (
          <div className="col-span-full flex items-center justify-center rounded-sm border border-neutral-blue-700 bg-neutral-blue-800 py-2 text-p-2 text-neutral-blue-200">
            Updating results...
          </div>
        )}
        {items.length > 0 ? (
          items.map((event) => (
            <div key={event.id}>
              <EventCard event={event} session={session} />
            </div>
          ))
        ) : (
          <p className="col-span-full">No events found.</p>
        )}

        {hasMore && (
          <div
            ref={loaderRef}
            className="col-span-full flex justify-center py-4"
          >
            {isLoadingMore ? (
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-neutral-blue-700 border-t-primary-500" />
            ) : (
              <Button variant="secondary" onClick={loadMore}>
                Load more
              </Button>
            )}
          </div>
        )}
      </div>

      <p
        className={cn(
          'col-span-full mt-6 text-right text-p-2 text-neutral-blue-300 desktop-xs:col-span-10 desktop-xs:col-start-2',
          total === 0 && 'hidden',
        )}
      >
        Showing {items.length} of {total}
      </p>
    </>
  )
}
